import { Users, MapPin, UserCheck, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

import type {
  IEmployeeCustomerMap,
  IEmployeeCustomerMapDetailed,
  IAreaCustomerMap,
  IClientCustomerMap,
} from '../types';
import EmployeeMappingTabs from './EmployeeMappingTabs';

interface EmployeeDetailsProps {
  selectedEmployee: IEmployeeCustomerMap | null;
  employeeDetails: IEmployeeCustomerMapDetailed | null;
  isLoading: boolean;
  error?: unknown;
  avatarFullPath: string;
  avatarServerPath?: string;
  employees: IEmployeeCustomerMap[];
  areas: IAreaCustomerMap[];
  clients: IClientCustomerMap[];
  clientsLoading: boolean;
  onRetry?: () => void;
}

export default function EmployeeDetails({
  selectedEmployee,
  employeeDetails,
  isLoading,
  error,
  avatarFullPath,
  avatarServerPath,
  employees,
  areas,
  clients,
  clientsLoading,
  onRetry,
}: EmployeeDetailsProps) {
  if (!selectedEmployee) {
    return (
      <Card className="min-h-[600px] border-white/20 bg-white/80 shadow-2xl backdrop-blur-xl dark:border-slate-700/50 dark:bg-slate-800/80">
        <CardHeader className="pb-6">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div className="rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 p-2">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                لوحة التحكم
              </CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-400">
                إدارة توزيع العملاء
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Welcome Section */}
          <div className="space-y-4 text-center">
            <div className="relative inline-block">
              <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-blue-500 to-purple-600 opacity-20 blur-2xl"></div>
              <div className="relative rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 p-6 dark:from-slate-700 dark:to-slate-600">
                <Users className="mx-auto h-16 w-16 text-slate-500 dark:text-slate-400" />
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-xl font-bold text-slate-900 dark:text-white">
                مرحباً بك في نظام توزيع العملاء
              </h3>
              <p className="mx-auto max-w-md text-slate-600 dark:text-slate-400">
                اختر موظف من القائمة على اليسار لعرض تفاصيل توزيع العملاء وإدارة
                المناطق المخصصة
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-4 dark:border-blue-700 dark:from-blue-900/30 dark:to-blue-800/30">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="rounded-lg bg-blue-500 p-2">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    إجمالي الموظفين
                  </p>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {new Intl.NumberFormat('en-US').format(employees.length)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-4 dark:border-green-700 dark:from-green-900/30 dark:to-green-800/30">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="rounded-lg bg-green-500 p-2">
                  <MapPin className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    المناطق المتاحة
                  </p>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">
                    {new Intl.NumberFormat('en-US').format(areas.length)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 p-4 dark:border-purple-700 dark:from-purple-900/30 dark:to-purple-800/30">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="rounded-lg bg-purple-500 p-2">
                  <UserCheck className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    مشاركة العملاء
                  </p>
                  <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    {new Intl.NumberFormat('en-US').format(
                      employees.filter(
                        (emp: IEmployeeCustomerMap) => emp.allowClientSharing,
                      ).length,
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100 p-4 dark:border-orange-700 dark:from-orange-900/30 dark:to-orange-800/30">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="rounded-lg bg-orange-500 p-2">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    إجمالي العملاء
                  </p>
                  <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                    {clientsLoading
                      ? '...'
                      : new Intl.NumberFormat('en-US').format(clients.length)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Important Warning and Instructions */}
          <div className="space-y-6">
            {/* Important Warning */}
            <div className="rounded-xl border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50 p-6 dark:border-orange-600 dark:from-orange-900/30 dark:to-amber-900/30">
              <div className="mb-4 flex items-center space-x-3 rtl:space-x-reverse">
                <div className="rounded-lg bg-orange-500 p-2">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <h4 className="text-lg font-bold text-orange-800 dark:text-orange-200">
                  ⚠️ تنبيه هام
                </h4>
              </div>
              <p className="text-sm leading-relaxed text-orange-700 dark:text-orange-300">
                هذه الصفحة مخصصة للموظفين فقط، وليست مخصصة للتيلي سيلز (جملة أو
                قطاعي).
              </p>
            </div>

            {/* Usage Instructions */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 p-6 dark:border-slate-700 dark:from-slate-800/50 dark:to-blue-900/20">
              <h4 className="mb-4 flex items-center space-x-3 font-semibold text-slate-900 dark:text-white rtl:space-x-reverse">
                <div className="rounded-lg bg-blue-500 p-2">
                  <UserCheck className="h-5 w-5 text-white" />
                </div>
                <span>آلية العمل والاستخدام:</span>
              </h4>

              <div className="mb-6 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/30">
                <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                  جميع العملاء المرتبطين بالموظف مباشرة، بالإضافة إلى المناطق
                  المخصصة له، تكون كل العملاء داخل هذه المناطق متاحة للموظف في:
                </p>

                <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <div className="rounded-md bg-white p-3 text-center shadow-sm dark:bg-slate-800">
                    <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      كارت العميل
                    </div>
                  </div>
                  <div className="rounded-md bg-white p-3 text-center shadow-sm dark:bg-slate-800">
                    <div className="text-xs font-medium text-green-600 dark:text-green-400">
                      إنشاء فواتير البيع
                    </div>
                  </div>
                  <div className="rounded-md bg-white p-3 text-center shadow-sm dark:bg-slate-800">
                    <div className="text-xs font-medium text-purple-600 dark:text-purple-400">
                      كشف الحساب
                    </div>
                  </div>
                  <div className="rounded-md bg-white p-3 text-center shadow-sm dark:bg-slate-800">
                    <div className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      الفواتير العامة
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                  باستثناء العملاء المحددين كمستثنين من هذا الموظف، فلا تظهر له
                  ولا يمكنه التعامل معهم.
                </p>
              </div>

              {/* General Access Warning */}
              <div className="rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 dark:border-amber-600 dark:from-amber-900/30 dark:to-yellow-900/30">
                <div className="mb-2 flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-lg">⚠️</span>
                  <h5 className="font-semibold text-amber-800 dark:text-amber-200">
                    ملاحظة هامة:
                  </h5>
                </div>
                <p className="text-sm leading-relaxed text-amber-700 dark:text-amber-300">
                  في حالة حصول الموظف على صلاحية كاملة (General Access)، فإن
                  جميع العملاء في النظام ستكون متاحة له، ما عدا العملاء المحددين
                  كمستثنين، حيث تظل صلاحياتهم محجوبة عنه.
                </p>
              </div>

              {/* Navigation Instructions */}
              <div className="mt-6 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <h5 className="font-medium text-slate-800 dark:text-slate-200">
                  التنقل في الصفحة:
                </h5>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <div className="flex items-start space-x-2 rtl:space-x-reverse">
                    <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"></div>
                    <p>استخدم البحث للعثور على موظف معين</p>
                  </div>
                  <div className="flex items-start space-x-2 rtl:space-x-reverse">
                    <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"></div>
                    <p>استخدم مفاتيح الأسهم للتنقل بين الموظفين</p>
                  </div>
                  <div className="flex items-start space-x-2 rtl:space-x-reverse">
                    <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"></div>
                    <p>اضغط Enter لاختيار الموظف وعرض تفاصيل التوزيع</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Component logic without excessive logging

  if (isLoading) {
    return (
      <Card className="hover:shadow-3xl min-h-[600px] border-white/20 bg-white/80 shadow-2xl backdrop-blur-xl transition-all duration-500 dark:border-slate-700/50 dark:bg-slate-800/80">
        <CardContent className="flex flex-col items-center justify-center py-20">
          <div className="relative mb-6">
            <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-blue-500 to-purple-600 opacity-30 blur-2xl"></div>
            <div className="relative h-16 w-16 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
          </div>
          <h3 className="mb-3 text-xl font-semibold text-slate-900 dark:text-white">
            جاري تحميل بيانات الموظف...
          </h3>
          <p className="max-w-md text-center text-slate-600 dark:text-slate-400">
            يتم جلب تفاصيل الموظف وبيانات التوزيع
          </p>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            <p>إذا استمر التحميل لأكثر من 10 ثوانٍ، يرجى:</p>
            <div className="mt-2 space-y-1">
              <p>• التحقق من اتصال الإنترنت</p>
              <p>• إعادة تحميل الصفحة</p>
              <p>• الاتصال بالدعم الفني</p>
            </div>
          </div>
          {onRetry && (
            <Button
              onClick={onRetry}
              className="mt-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700"
            >
              إعادة المحاولة
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="hover:shadow-3xl min-h-[600px] border-white/20 bg-white/80 shadow-2xl backdrop-blur-xl transition-all duration-500 dark:border-slate-700/50 dark:bg-slate-800/80">
        <CardContent className="flex flex-col items-center justify-center py-20">
          <div className="relative mb-6">
            <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-red-500 to-orange-600 opacity-30 blur-2xl"></div>
            <div className="relative rounded-2xl bg-gradient-to-br from-red-100 to-orange-100 p-6 dark:from-red-900/30 dark:to-orange-900/30">
              <svg
                className="h-12 w-12 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>
          <h3 className="mb-3 text-xl font-semibold text-slate-900 dark:text-white">
            حدث خطأ في تحميل البيانات
          </h3>
          <p className="max-w-md text-center text-slate-600 dark:text-slate-400">
            تعذر تحميل تفاصيل الموظف. يرجى المحاولة مرة أخرى.
          </p>
          <Button
            onClick={onRetry}
            className="mt-4 bg-gradient-to-r from-red-500 to-orange-600 text-white hover:from-red-600 hover:to-orange-700"
          >
            إعادة المحاولة
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!employeeDetails) {
    return (
      <Card className="hover:shadow-3xl min-h-[600px] border-white/20 bg-white/80 shadow-2xl backdrop-blur-xl transition-all duration-500 dark:border-slate-700/50 dark:bg-slate-800/80">
        <CardContent className="flex flex-col items-center justify-center py-20">
          <div className="relative mb-6">
            <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-red-500 to-orange-600 opacity-30 blur-2xl"></div>
            <div className="relative rounded-2xl bg-gradient-to-br from-red-100 to-orange-100 p-6 dark:from-red-900/30 dark:to-orange-900/30">
              <Users className="h-16 w-16 text-red-500 dark:text-red-400" />
            </div>
          </div>
          <h3 className="mb-3 text-xl font-semibold text-slate-900 dark:text-white">
            خطأ في تحميل البيانات
          </h3>
          <p className="max-w-md text-center text-slate-600 dark:text-slate-400">
            لم يتم العثور على بيانات الموظف أو حدث خطأ في التحميل
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-3xl min-h-[500px] border-white/20 bg-white/80 shadow-2xl backdrop-blur-xl transition-all duration-500 dark:border-slate-700/50 dark:bg-slate-800/80">
      <CardHeader className="pb-4">
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 opacity-75 blur-xl"></div>
            <Avatar className="border-3 relative h-16 w-16 border-white shadow-xl dark:border-slate-700">
              <AvatarImage
                src={
                  selectedEmployee.avatar
                    ? `${avatarFullPath}${selectedEmployee.avatar}`
                    : undefined
                }
                alt={selectedEmployee.empName}
              />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-xl font-bold text-white">
                {selectedEmployee.empName
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .slice(0, 2)}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="flex-1">
            <CardTitle className="bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-xl font-bold text-transparent dark:from-white dark:to-slate-200">
              {selectedEmployee.empName}
            </CardTitle>
            <CardDescription className="mt-1 text-base text-slate-600 dark:text-slate-300">
              {selectedEmployee.jobName}
            </CardDescription>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <Badge
                variant="soft"
                className="border-blue-200 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 dark:border-blue-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:text-blue-300"
              >
                {selectedEmployee.departmentName}
              </Badge>
              <Badge
                variant="outline"
                className="border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300"
              >
                {selectedEmployee.departmentCode}
              </Badge>
              <Badge
                variant={employeeDetails.isFullAccess ? 'soft' : 'outline'}
                className={
                  employeeDetails.isFullAccess
                    ? 'border-green-200 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:border-green-700 dark:from-green-900/50 dark:to-emerald-900/50 dark:text-green-300'
                    : 'border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300'
                }
              >
                {employeeDetails.isFullAccess
                  ? 'صلاحيات كاملة'
                  : 'صلاحيات محدودة'}
              </Badge>
              <Badge
                variant={employeeDetails.hasMapping ? 'soft' : 'outline'}
                className={
                  employeeDetails.hasMapping
                    ? 'border-blue-200 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 dark:border-blue-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:text-blue-300'
                    : 'border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300'
                }
              >
                {employeeDetails.hasMapping ? 'لديه توزيع' : 'لا يوجد توزيع'}
              </Badge>
              {employeeDetails.email && (
                <Badge variant="outline" className="text-xs">
                  📧 {employeeDetails.email}
                </Badge>
              )}
              {employeeDetails.telePhone && (
                <Badge variant="outline" className="text-xs">
                  📱 {employeeDetails.telePhone}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enhanced Employee Details Section */}
        <div>
          <h3 className="mb-4 flex items-center space-x-2 text-lg font-semibold rtl:space-x-reverse">
            <div className="rounded-md bg-gradient-to-br from-blue-500 to-purple-600 p-1.5">
              <UserCheck className="h-4 w-4 text-white" />
            </div>
            <span className="bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent dark:from-white dark:to-slate-200">
              تفاصيل الموظف
            </span>
          </h3>

          <div className="space-y-4">
            {/* Client and Area Counts */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-3 dark:border-blue-700 dark:from-blue-900/30 dark:to-blue-800/30">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <div className="rounded-md bg-blue-500 p-1.5">
                    <Users className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      العملاء المخصصين
                    </p>
                    <p className="text-base font-bold text-blue-600 dark:text-blue-400">
                      {employeeDetails.clients?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-3 dark:border-green-700 dark:from-green-900/30 dark:to-green-800/30">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <div className="rounded-md bg-green-500 p-1.5">
                    <MapPin className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      المناطق المخصصة
                    </p>
                    <p className="text-base font-bold text-green-600 dark:text-green-400">
                      {employeeDetails.areas?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 p-3 dark:border-purple-700 dark:from-purple-900/30 dark:to-purple-800/30">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <div className="rounded-md bg-purple-500 p-1.5">
                    <UserCheck className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      العملاء المستبعدين
                    </p>
                    <p className="text-base font-bold text-purple-600 dark:text-purple-400">
                      {employeeDetails.excludeClients?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Employee Mapping Tabs */}
        <EmployeeMappingTabs
          selectedEmployee={selectedEmployee}
          employeeDetails={employeeDetails}
          clients={clients}
          areas={areas}
          avatarServerPath={avatarServerPath}
          onMappingUpdated={() => {
            // This will be handled by the query invalidation in the tabs component
          }}
        />
      </CardContent>
    </Card>
  );

  // Fallback for when employee details are not available but employee is selected
  if (selectedEmployee && !employeeDetails && !isLoading && !error) {
    return (
      <Card className="hover:shadow-3xl min-h-[500px] border-white/20 bg-white/80 shadow-2xl backdrop-blur-xl transition-all duration-500 dark:border-slate-700/50 dark:bg-slate-800/80">
        <CardHeader className="pb-4">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 opacity-75 blur-xl"></div>
              <Avatar className="border-3 relative h-16 w-16 border-white shadow-xl dark:border-slate-700">
                <AvatarImage
                  src={
                    selectedEmployee?.avatar
                      ? `${avatarFullPath}${selectedEmployee?.avatar}`
                      : undefined
                  }
                  alt={selectedEmployee?.empName || ''}
                />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-xl font-bold text-white">
                  {selectedEmployee?.empName
                    ?.split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .slice(0, 2) || '??'}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1">
              <CardTitle className="bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-xl font-bold text-transparent dark:from-white dark:to-slate-200">
                {selectedEmployee?.empName || 'موظف غير معروف'}
              </CardTitle>
              <CardDescription className="mt-1 text-base text-slate-600 dark:text-slate-300">
                {selectedEmployee?.jobName || 'وظيفة غير محددة'}
              </CardDescription>
              <div className="mt-3 flex items-center space-x-2 rtl:space-x-reverse">
                <Badge
                  variant="soft"
                  className="border-blue-200 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 dark:border-blue-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:text-blue-300"
                >
                  {selectedEmployee?.departmentName || 'قسم غير محدد'}
                </Badge>
                <Badge
                  variant="outline"
                  className="border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300"
                >
                  {selectedEmployee?.departmentCode || 'N/A'}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="text-center">
            <div className="relative mb-6 inline-block">
              <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 opacity-30 blur-2xl"></div>
              <div className="relative rounded-2xl bg-gradient-to-br from-yellow-100 to-orange-100 p-6 dark:from-yellow-900/30 dark:to-orange-900/30">
                <svg
                  className="h-12 w-12 text-yellow-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="mb-3 text-xl font-semibold text-slate-900 dark:text-white">
              لا يوجد توزيع للعملاء
            </h3>
            <p className="mx-auto max-w-md text-slate-600 dark:text-slate-400">
              هذا الموظف لا يملك توزيع للعملاء بعد. يمكنك إنشاء توزيع جديد من
              خلال التبويبات أدناه.
            </p>
          </div>

          {/* Employee Mapping Tabs for creating new mapping */}
          {selectedEmployee && (
            <EmployeeMappingTabs
              selectedEmployee={selectedEmployee!}
              employeeDetails={null}
              clients={clients}
              areas={areas}
              avatarServerPath={avatarServerPath}
              onMappingUpdated={() => {
                // This will be handled by the query invalidation in the tabs component
              }}
            />
          )}
        </CardContent>
      </Card>
    );
  }
}
